import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import { PlusIcon, MinusIcon} from '@heroicons/react/24/outline'
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid"
import PulseLoader from "react-spinners/PulseLoader"

import {
  useGetUsersQuery,
  useUpdateUserMutation,
  useDeactivateUserMutation, // 30 Aug 2026
} from "./usersApiSlice"

import { useGetOrganizationsQuery } from "../organizations/organizationsApiSlice"
import { useGetOrgRolesQuery } from "../orgRoles/orgRolesApiSlice" // 06 Sep 2024
import { useGetOrgTitlesQuery } from "../orgTitles/orgTitlesApiSlice" // 06 Sep 2024

import { useGetOrganizationBUsQuery } from "../organizationBUs/organizationBUsApiSlice"
import { useGetAccountsQuery } from "../accounts/accountsApiSlice"

// 29 Apr 2026
import {
  useGetUserOrgBUMapsQuery,
  useCreateNewUserOrgBUMapMutation,
  useUpdateUserOrgBUMapMutation,
} from "../userOrgBUMaps/userOrgBUMapsApiSlice"

import {
  useGetUserAccountMapsQuery,
  useCreateNewUserAccountMapMutation,
  useUpdateUserAccountMapMutation,
} from "../userAccountMaps/userAccountMapsApiSlice"


const EtUpdateOrgUser = ({id, setActiveAdminView}) => {
  // First Created - 31 Jul 2024
  // Author - Atul Pandey
  // Updated - 06 Sep 2024 - for user org role and org title
  // Updated - 02 Mar 2025 - updated the comp with props - { id, setActiveAdminView}
  // Updated - 02 Mar 2025 - switched from navigate to setActiveAdminView
  // Updated - 12 May 2025 - added labels to form fields
  // Updated - 29 Apr 2026 - refined for org BU and accounts updates
  // Updated - 30 Aug 2026 - refined for deactivating user
  // Updated - 08 Sep 2026 - refined for allowed org roles list expansion

  // 1 - Set Page Title
  useTitle('Update A User')
  // console.log('incoming id in EtUpdateOrgUser', id)

  // 2 - Extract the Id to be updated
  // const { id } = useParams()
  // console.log("id:", id)

  // 3 - Extract the user to be updated
  const { user } = useGetUsersQuery("User", {
    selectFromResult: ({ data }) => ({
      user: data?.entities[id],
    }),
  })
  // console.log('user', user)

  // 3a - Extract the roles enum list to be used for updating roles -- 01 Aug 2024
  const { rolesEnum } = useGetUsersQuery("User", {
    selectFromResult: ({ data }) => ({
      rolesEnum: data?.rolesEnum,
    }),
  })
  // console.log('roles Enum:', rolesEnum)
   
  // 4 - Check for returned data
  if (!user) {
    return <PulseLoader color={"#ea580c"} />
  }

  // 5. Display data
  let content
  content = <EtUpdateUserForm user={user} id={id} roles={rolesEnum} setActiveAdminView={setActiveAdminView}/> // 02 Mar 2025 - added setActiveAdminView={setActiveAdminView}  

  return content
}

const EtUpdateUserForm = ({user, id, roles, setActiveAdminView}) => {

  // Author - Atul Pandey
  // Updated - 01 Aug 2024 - added roles from rolesEnum - add , remove and select all features
  // Updated - 01 Aug 2024 - added organization assignment
  // Updated - 06 Sep 2024 - for user org role and org title
  // Updated - 13 Mar 2025 - added googleIcalUrl
  // Updated - 14 Mar 2025 - show / hide googleIcalUrl visibility
  // Updated - 12 May 2025 - added labels to form fields
  // Updated - 29 Apr 2026 - refined for org BU and accounts updates
  // Updated - 30 Aug 2026 - refined for deactivating user
  

  // 1 - set the variable for navigate
  const navigate = useNavigate()

  // 2a - Extract the organizations data
  const {data:organizationsData} = useGetOrganizationsQuery()
  // console.log('organizations data', organizationsData)

  // 2b - Extract the organization Names from the org data
  const orgInfo = organizationsData?.ids.map((id) => {
    const organization = organizationsData?.entities[id]
    return {orgName:organization.organizationName,orgId:organization._id }
  })
  // console.log('Organization:', orgInfo)

  // 2c - Extract the org roles data -- 06 Sep 2024
  const {data: orgRolesData} = useGetOrgRolesQuery()
  const orgRoles = orgRolesData?.ids?.map((id) => {
    const orgRole = orgRolesData?.entities[id]
    return orgRole
  })
  // console.log('OrgRoles:', orgRoles)

  // 2d - Extract the org titles data -- 06 Sep 2024
  const {data: orgTitlesData} = useGetOrgTitlesQuery()
  const orgTitles = orgTitlesData?.ids?.map((id) => {
    const orgTitle = orgTitlesData?.entities[id]
    return orgTitle
  })
  // console.log('OrgTitles:', orgTitles)

  // 2e - Define the mutation to update the user
  const [updateUser, { isLoading, isSuccess, isError, error }] = useUpdateUserMutation()

  // 30 Aug 2026 - Activate / Deactivate User
  const [
    deactivateUser,
    {
      isLoading: isDeactivating,
      isError: isDeactivateError,
      error: deactivateError,
    },
  ] = useDeactivateUserMutation()

  // 29 Apr 2026
  // 2e1 - Extract Organization BUs
  const { data: organizationBUsData } = useGetOrganizationBUsQuery()

  const organizationBUs = organizationBUsData?.ids?.map((id) => {
    return organizationBUsData?.entities[id]
  })

  // 2e2 - Extract Accounts
  const { data: accountsData } = useGetAccountsQuery()

  const accounts = accountsData?.ids?.map((id) => {
    return accountsData?.entities[id]
  })

  // 2e3 - Extract existing active BU maps for this user
  const { data: userOrgBUMapsData } = useGetUserOrgBUMapsQuery({
    userId: id,
    isActive: true,
  })

  const userOrgBUMaps = userOrgBUMapsData?.ids?.map((mapId) => {
    return userOrgBUMapsData?.entities[mapId]
  })

  const primaryBUMap = userOrgBUMaps?.find(map => map?.isPrimaryBU === true)

  // 2e4 - Extract existing active Account maps for this user
  const { data: userAccountMapsData } = useGetUserAccountMapsQuery({
    userId: id,
    isActive: true,
  })

  const userAccountMaps = userAccountMapsData?.ids?.map((mapId) => {
    return userAccountMapsData?.entities[mapId]
  })

  const primaryAccountMap = userAccountMaps?.find(map => map?.isPrimaryAccount === true)

  // 2e5 - Map mutations
  const [createNewUserOrgBUMap] = useCreateNewUserOrgBUMapMutation()
  const [updateUserOrgBUMap] = useUpdateUserOrgBUMapMutation()

  const [createNewUserAccountMap] = useCreateNewUserAccountMapMutation()
  const [updateUserAccountMap] = useUpdateUserAccountMapMutation()

  // 2f - Cal url visibility - 14 Mar 2025
  const [isIcalVisible, setIsIcalVisible] = useState(false); // ✅ Toggle visibility

  // 3a - Fill the form data with extracted user info
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    username: user.username,
    roles: user.roles || [], // Initialize assigned roles // 01 Aug
    userOrgId: user?.userOrgId?._id || null, // 01 Aug
    userOrgRole: user?.userOrgRole?._id || null, // 06 Sep 2024
    userOrgTitle: user?.userOrgTitle?._id || null, // 06 Sep 2024
    googleIcalUrl: user.googleIcalUrl || "", // 13 Mar 2025 - Added Google iCal URL

    // 29 Apr 2026
    currentOrgBUId: user?.currentOrgBUId?._id || user?.currentOrgBUId || "",
    currentAccountId: user?.currentAccountId?._id || user?.currentAccountId || "",
  });
  
  // const [availableRoles, setAvailableRoles] = useState(
  //   roles.filter((role) => !formData.roles.includes(role))
  // );
  
  // 3b - Determine the span of allowed Roles for OrgAdmin based on Org
  const isEternityOrg = organizationsData?.entities?.[user?.userOrgId?._id]?.organizationName
  ?.toLowerCase()
  ?.includes("eternity");

  const allowedOrgRoles = [
    "Guest", 
    "OrgUser", 
    "OrgCoach", 
    "OrgSponsor", 
    "OrgAdmin", 
    // 08 Sep 2026
    "OrgCxO",
    "OrgBULeader",
    "OrgLeader",
    "OrgFacilitator",
  ];
  const filteredRoles = isEternityOrg ? roles : roles.filter(role => allowedOrgRoles.includes(role));

  const [availableRoles, setAvailableRoles] = useState(
    filteredRoles.filter(role => !formData.roles.includes(role))
  );

  // console.log('Available Roles', availableRoles)

  // 4 - Reset form upon successful submission
  useEffect(() => {
    if(isSuccess) {
      setFormData({
        name:"",
        email:"",
        username:"",
        roles:[],
        userOrgId:"",
        googleIcalUrl:""
      })
      // navigate('/users')
      setActiveAdminView('orgUsers'); // 02 Mar 2025
    }
    
    },[isSuccess])// ✅ 02 Mar 2025 - Removed `navigate` since it's no longer used.

    // 5a - Form field updates / change handling
  const handleChange = (e) => {
    const { name, value, files } = e.target
    if (name === "pic") {
      setFormData(prevState => ({ ...prevState, pic: files[0] }))
    }  else {
      setFormData(prevState => ({ ...prevState, [name]: value }))
    }
  }

  // 5b - Handle Add Role
  const handleAddRole = (role) => {
    setFormData(prevState => ({
      ...prevState,
      roles: [...prevState.roles, role],
    }));
    setAvailableRoles(prevRoles => prevRoles.filter(r => r !== role));
  };

  // 5c - Handle Remove Roles
  const handleRemoveRole = (index) => {
    setFormData(prevState => {
      const updatedRoles = prevState.roles.filter((_, i) => i !== index);
      return { ...prevState, roles: updatedRoles };
    });
    setAvailableRoles(prevRoles => [...prevRoles, formData.roles[index]]);
  };

  // 5d - Organization change
  const handleOrgChange = (e) => {
    setFormData(prevState => ({
      ...prevState,
      userOrgId: e.target.value
    }));
  };

  // 5e - Handle Select All
  const handleSelectAllRoles = () => {
    setFormData(prevState => ({
      ...prevState,
      // roles: roles, // Assign all roles
      roles: filteredRoles, // 12 May 2025
    }));
    setAvailableRoles([]);
  };
  
  // 6 - Form submit handling
  const handleSubmit = async (e) => {
    e.preventDefault()
  
    // Initialize an object to collect the form data
    const userData = {
      name:formData.name,
      email:formData.email,
      username:formData.username,
      roles: formData.roles,
      userOrgId: formData.userOrgId,
      userOrgRole: formData.userOrgRole, // 06 Sep 2024
      userOrgTitle: formData.userOrgTitle, // 06 Sep 2024
      googleIcalUrl: formData.googleIcalUrl, // 13 Mar 2025 - Added Google iCal URL
    }
  
    //console.log('user Name:',userData.userName)
  
    if (formData?.pic) {
      // Convert the image to a base64 string
      const reader = new FileReader()
      reader.onloadend = async () => {
        // Once conversion is done, add the base64 string to your data object
        userData.pic = reader.result
  
        // Now, send the data object to the server
        try {
          userData.id = id
          console.log("Form submitted.")
          await updateUser(userData).unwrap() // Dispatch the RTK Query mutation
        } catch (error) {
          console.error(error)
        }
      }
      reader.readAsDataURL(formData.pic)
    } else {
      // No image, send other data as is
      try {
        userData.id = id
        console.log("Form submitted.")
        await updateUser(userData).unwrap() // Dispatch the RTK Query mutation

        // 29 Apr 2026 - refinements for Org BU and Account updates

        let resolvedUserOrgBUMapId = primaryBUMap?._id || null

        // 6a - Update / Create primary BU map
        if (formData.currentOrgBUId) {
          if (primaryBUMap?._id) {
            await updateUserOrgBUMap({
              userOrgBUMapId: primaryBUMap._id,
              orgBUId: formData.currentOrgBUId,
              isPrimaryBU: true,
              isActive: true,
            }).unwrap()

            resolvedUserOrgBUMapId = primaryBUMap._id
          } else {
            const createdBUMap = await createNewUserOrgBUMap({
              userId: id,
              orgId: formData.userOrgId,
              orgBUId: formData.currentOrgBUId,
              isPrimaryBU: true,
              isActive: true,
              startDate: new Date(),
            }).unwrap()

            resolvedUserOrgBUMapId = createdBUMap?._id
          }
        }

        // 6b - Update / Create primary account map
        if (formData.currentAccountId) {
          if (primaryAccountMap?._id) {
            await updateUserAccountMap({
              userAccountMapId: primaryAccountMap._id,
              orgBUId: formData.currentOrgBUId || null,
              accountId: formData.currentAccountId,
              isPrimaryAccount: true,
              isActive: true,
            }).unwrap()
          } else {
            await createNewUserAccountMap({
              userId: id,
              orgId: formData.userOrgId,
              orgBUId: formData.currentOrgBUId || null,
              // userOrgBUMapId: primaryBUMap?._id || null,
              userOrgBUMapId: resolvedUserOrgBUMapId,
              accountId: formData.currentAccountId,
              isPrimaryAccount: true,
              isActive: true,
              startDate: new Date(),
            }).unwrap()
          }
        }

      } catch (error) {
        console.error(error)
      }
    }
  }
  // 7 - Handle Cancel
  const handleCancel = () => {
    // navigate('/orgUsers')
    setActiveAdminView('orgUsers'); // 02 Mar 2025
  }

  // 30 Aug 2026 - Activate / Deactivate User
  const handleDeactivateUser = async () => {

    const action =
      user?.active
        ? "deactivate"
        : "activate"

    const confirmed =
      window.confirm(
        `Are you sure you want to ${action} ${user?.name}?`
      )

    if (!confirmed) {
      return
    }

    try {

      await deactivateUser({
        id,
      }).unwrap()

      setActiveAdminView(
        'orgUsers'
      )

    } catch (error) {

      console.error(
        `Error trying to ${action} user:`,
        error
      )

    }
  }
  
  // 8 - Handle Clear
    const handleClear = () => {
      setFormData({
        name:"",
        email:"",
        username:"",
        roles:[],
        userOrgId:"",
        googleIcalUrl:""

      })
    }
  
    return (
      <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black font-poppins">
        {isLoading && <PulseLoader color={"#ea580c"} />}
        {isError && (
          <p className="text-orange-700 font-semi-bold py-2">
            {error?.data?.message}
          </p>
        )}

        {/* 30 Aug 2026 */}
        {isDeactivating && (
          <PulseLoader color={"#ea580c"} />
        )}

        {isDeactivateError && (
          <p className="text-orange-700 font-semi-bold py-2">
            {deactivateError?.data?.message}
          </p>
        )}


        <div className="bg-white p-1 m-1 rounded">
          <p className="p-1 m-1 font-bold font-poppins text-xl">Update a Org User</p>
    
          <div className="bg-gray-200 rounded m-1 p-1">
            <form
              onSubmit={handleSubmit}
              className="m-1 p-1 text-xs font-poppins"
            >
              
              <div id="name" className="mr-2">
                <label htmlFor="name" className="block text-sm font-bold text-gray-700 px-1 mx-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter the user's name here"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full rounded p-2 m-1 bg-gray-50 text-gray-400"
                  disabled={true}
                />
              </div>

              <div id="email" className="mr-2">
                <label htmlFor="email" className="block text-sm font-bold text-gray-700 px-1 mx-1">
                  Email
                </label>
                <input
                  type="text"
                  name="email"
                  placeholder="Enter the user's email here"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full rounded p-2 m-1 bg-gray-50 text-gray-400"
                  disabled={true}
                />
              </div>

              <div id="username" className="mr-2">
                <label htmlFor="username" className="block text-sm font-bold text-gray-700 px-1 mx-1">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  placeholder="Enter the user's username here"
                  value={formData.username}
                  onChange={handleChange}
                  className="w-full rounded p-2 m-1 bg-gray-50 text-gray-400"
                  disabled={true}
                />
              </div>

              {/* 13 Mar 2025 - Add Google iCal URL Field */}
              {/* <div id="googleIcalUrl" >
                <label htmlFor="googleIcalUrl" className="block text-sm font-bold text-gray-700 px-1 mx-1">
                  Google Calendar Url
                </label>
                <div className="relative">
                  <input
                    // type={isIcalVisible ? "text" : "password"}
                    type={"text"}
                    name="googleIcalUrl"
                    placeholder="Enter Google Calendar iCal URL"
                    // value={formData.googleIcalUrl}
                    value={isIcalVisible ? formData.googleIcalUrl : "•••••••••"}
                    onChange={handleChange}
                    className="w-full rounded p-2 m-1 pr-10"
                    // className={`w-full rounded p-2 m-1 pr-10 ${isIcalVisible ? "break-words pr-4" : "tracking-widest text-gray-400"}`} 
                    // className={`w-full rounded p-2 m-1 pr-10 break-words overflow-hidden text-ellipsis whitespace-pre-wrap ${isIcalVisible ? "pr-4" : "tracking-widest text-gray-400"}`} 
                    readOnly={!isIcalVisible} // ✅ Prevent user from editing when masked
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center"
                    onClick={() => setIsIcalVisible(prev => !prev)}
                  >
                    {isIcalVisible ? (
                      <EyeSlashIcon className="h-4 w-4 text-orange-600" /> // ✅ Hide
                    ) : (
                      <EyeIcon className="h-4 w-4 text-orange-600" /> // ✅ Show
                    )}
                  </button>
                </div>
                
              </div> */}

              {/* Roles Section */}
              <div id="roles" className="bg-white rounded p-2 m-1 font-poppins">
                <p className="font-semibold m-1 p-1">Roles:</p>
                {formData.roles?.map((role, index) => (
                  <div key={index} className="flex items-center justify-start mx-1 px-1">
                    <button type="button" onClick={() => handleRemoveRole(index)} className="bg-red-400 hover:bg-red-600 rounded p-1">
                      <MinusIcon className="h-5 w-5 text-white" />
                    </button>
                    <div className="m-1 p-1 border rounded w-full"><span className="px-1">{role}</span></div>
                  </div>
                ))}
                {availableRoles.length > 0 && (
                  <div className="flex justify-start items-center">
                    {/* Added - Select All Button */}
                    <div className="">
                      <button
                        type="button"
                        onClick={handleSelectAllRoles}
                        className="bg-lime-500 hover:bg-lime-700 text-white rounded p-2"
                      >
                        Select All
                      </button>
                    </div>
                    <div className="">
                      <select onChange={(e) => handleAddRole(e.target.value)} className="rounded p-2 m-1 border">
                        <option value="">Select a role to add</option>
                        {availableRoles.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Organization Section */}
              {/* <div id="organization" className="bg-white rounded p-2 m-1 font-poppins">
                <p className="font-semibold m-1 p-1">Organization:</p>
                <select
                  name="organization"
                  value={formData.userOrgId}
                  onChange={handleOrgChange}
                  className="rounded p-2 m-1 border"
                >
                  <option value="">Select an organization</option>
                  {orgInfo?.map(org => (
                    <option key={org?.orgId} value={org.orgId}>{org.orgName}</option>
                  ))}
                </select>
              </div> */}

              {/* Org Role Section */}
              <div id="orgRole" className="bg-white rounded p-2 m-1 font-poppins">
                <p className="font-semibold m-1 p-1">Organization Role:</p>
                <select
                  name="orgRole"
                  value={formData?.userOrgRole}
                  onChange={(e) => setFormData(prevState => ({ ...prevState, userOrgRole: e.target.value }))}
                  className="rounded p-2 m-1 border"
                >
                  <option value="">Select an organization role</option>
                  {orgRoles?.map(role => (
                    <option key={role?._id} value={role._id}>{role.orgRoleName}</option>
                  ))}
                </select>
              </div>

              {/* Org Title Section */}
              <div id="orgTitle" className="bg-white rounded p-2 m-1 font-poppins">
                <p className="font-semibold m-1 p-1">Organization Title:</p>
                <select
                  name="orgTitle"
                  value={formData?.userOrgTitle}
                  onChange={(e) => setFormData(prevState => ({ ...prevState, userOrgTitle: e.target.value }))}
                  className="rounded p-2 m-1 border"
                >
                  <option value="">Select an organization title</option>
                  {orgTitles?.map(title => (
                    <option key={title?._id} value={title._id}>{title.orgTitleName}</option>
                  ))}
                </select>
              </div>


              {/* 29 Apr 2026 - Org BU and Account Updates Section */}

              {/* Organization BU Section */}
              <div id="organizationBU" className="bg-white rounded p-2 m-1 font-poppins">
                <p className="font-semibold m-1 p-1">Organization BU:</p>

                <select
                  name="currentOrgBUId"
                  value={formData?.currentOrgBUId}
                  onChange={(e) =>
                    setFormData(prevState => ({
                      ...prevState,
                      currentOrgBUId: e.target.value,
                    }))
                  }
                  className="rounded p-2 m-1 border"
                >
                  <option value="">Select an organization BU</option>

                  {organizationBUs
                    ?.filter(bu => {
                      const buOwnerOrgId = bu?.ownerOrgId?._id || bu?.ownerOrgId
                      return String(buOwnerOrgId) === String(formData.userOrgId)
                    })
                    ?.map(bu => (
                      <option key={bu?._id} value={bu?._id}>
                        {bu?.organizationBUName}
                      </option>
                    ))}
                </select>
              </div>

              {/* Primary Account Section */}
              <div id="primaryAccount" className="bg-white rounded p-2 m-1 font-poppins">
                <p className="font-semibold m-1 p-1">Primary Account:</p>

                <select
                  name="currentAccountId"
                  value={formData?.currentAccountId}
                  onChange={(e) =>
                    setFormData(prevState => ({
                      ...prevState,
                      currentAccountId: e.target.value,
                    }))
                  }
                  className="rounded p-2 m-1 border"
                >
                  <option value="">Select a primary account</option>

                  {accounts
                    ?.filter(account => {
                      const accountOwnerOrgId = account?.ownerOrgId?._id || account?.ownerOrgId
                      return String(accountOwnerOrgId) === String(formData.userOrgId)
                    })
                    ?.map(account => (
                      <option key={account?._id} value={account?._id}>
                        {account?.accountName}
                      </option>
                    ))}
                </select>
              </div>

              {/* 30 Aug 2026 */}
              {/* Activate / Deactivate is always available */}
              <div className="bg-white m-1 px-1 py-3 rounded">

                <button
                  type="button"
                  onClick={handleDeactivateUser}
                  disabled={isDeactivating}
                  className={`
                    btn
                    border
                    bg-transparent
                    w-40
                    p-2
                    rounded-full
                    mx-1
                    transition-all

                    ${
                      user?.active
                        ? `
                          border-red-600
                          text-red-600
                          hover:bg-gradient-to-r
                          hover:from-red-800
                          hover:to-red-600
                          hover:border-transparent
                          hover:text-white
                          hover:shadow-lg
                          hover:shadow-black
                        `
                        : `
                          border-lime-600
                          text-lime-700
                          hover:bg-gradient-to-r
                          hover:from-lime-600
                          hover:to-lime-800
                          hover:border-transparent
                          hover:text-white
                          hover:shadow-lg
                          hover:shadow-black
                        `
                    }
                  `}
                >
                  {isDeactivating
                    ? "Updating..."
                    : user?.active
                      ? "Deactivate User"
                      : "Activate User"
                  }
                </button>

              </div>


              {/* Other actions only available for active users */}
              {user?.active && (
                <div
                  id="actions"
                  className="bg-white m-1 px-1 py-3 rounded"
                >
                  <EtBtnFormBasic
                    name={"Update"}
                    type={"create"}
                    handleClick={handleSubmit}
                  />

                  <EtBtnFormBasic
                    name={"Cancel"}
                    type={"cancel"}
                    handleClick={handleCancel}
                  />
                </div>
              )}

             {/* <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
                <EtBtnFormBasic
                  name={"Update"}
                  type={"create"}
                  handleClick={handleSubmit}
                />
                
                <button
                  type="button"
                  onClick={handleDeactivateUser}
                  disabled={isDeactivating}
                  className={`
                    btn
                    border
                    bg-transparent
                    w-40
                    p-2
                    rounded-full
                    mx-1
                    transition-all

                    ${
                      user?.active
                        ? `
                          border-red-600
                          text-red-600
                          hover:bg-gradient-to-r
                          hover:from-red-800
                          hover:to-red-600
                          hover:border-transparent
                          hover:text-white
                          hover:shadow-lg
                          hover:shadow-black
                        `
                        : `
                          border-lime-600
                          text-lime-700
                          hover:bg-gradient-to-r
                          hover:from-lime-600
                          hover:to-lime-800
                          hover:border-transparent
                          hover:text-white
                          hover:shadow-lg
                          hover:shadow-black
                        `
                    }

                    disabled:border-gray-400
                    disabled:text-gray-400
                    disabled:cursor-not-allowed
                  `}
                >
                  {isDeactivating
                    ? "Updating..."
                    : user?.active
                      ? "Deactivate User"
                      : "Activate User"
                  }
                </button>

                <EtBtnFormBasic
                  name={"Cancel"}
                  type={"cancel"}
                  handleClick={handleCancel}
                />
              </div> */}
            </form>
          </div>
        </div>
      </div>
    )


}

export default EtUpdateOrgUser